import { useState, useRef, useCallback, useEffect } from 'react';
import { X, Upload, FileText, Image, File, CheckCircle, AlertCircle } from 'lucide-react';
import api from '../../api/config';

const DOC_TYPES = [
  { value: 'cotizacion', label: 'Cotización / Quote' },
  { value: 'orden_compra_cliente', label: 'Orden de Compra Cliente / Client PO' },
  { value: 'orden_compra_proveedor', label: 'Orden de Compra Proveedor / Supplier PO' },
  { value: 'guia_despacho', label: 'Guía de Despacho / Dispatch Guide' },
  { value: 'factura_cliente', label: 'Factura Cliente / Client Invoice' },
  { value: 'factura_proveedor', label: 'Factura Proveedor / Supplier Invoice' },
  { value: 'otro', label: 'Otro / Other' }
];

const DOC_REF_LABELS = {
  cotizacion: { label: 'N° Cotización / Quote Number', placeholder: 'Ej: COT-2024-001' },
  orden_compra_cliente: { label: 'N° Orden de Compra / PO Number', placeholder: 'Ej: OC-12345' },
  orden_compra_proveedor: { label: 'N° OC Proveedor / Supplier PO Number', placeholder: 'Ej: OC-PROV-001' },
  guia_despacho: { label: 'N° Guía de Despacho / Guide Number', placeholder: 'Ej: GD-54321' },
  factura_cliente: { label: 'N° Factura / Invoice Number', placeholder: 'Ej: FAC-12345' },
  factura_proveedor: { label: 'N° Factura Proveedor / Supplier Invoice', placeholder: 'Ej: FAC-PROV-001' },
};

const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB

const DocumentUploadModal = ({ isOpen, onClose, itemNumber, itemData, onUploadSuccess, defaultDocType }) => {
  const [file, setFile] = useState(null);
  const [docType, setDocType] = useState('');
  const [docReference, setDocReference] = useState('');
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  // Pre-select doc type when defaultDocType is provided
  useEffect(() => {
    if (isOpen && defaultDocType) {
      handleDocTypeChange(defaultDocType);
    }
  }, [isOpen, defaultDocType]);

  // Auto-populate reference based on doc type
  const handleDocTypeChange = (type) => {
    setDocType(type);
    if (itemData) {
      switch (type) {
        case 'cotizacion':
          setDocReference(itemData.cotizacion || '');
          break;
        case 'orden_compra_cliente':
          setDocReference(itemData.p_order || '');
          break;
        case 'orden_compra_proveedor':
          setDocReference(itemData.oc_sobek || '');
          break;
        case 'guia_despacho':
          setDocReference(itemData.guia || '');
          break;
        case 'factura_cliente':
          setDocReference(itemData.factura || '');
          break;
        case 'factura_proveedor':
          setDocReference(itemData.factura_proveedor || '');
          break;
        default:
          setDocReference('');
      }
    }
  };

  const validateFile = (f) => {
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(f.type)) {
      setError('Solo se permiten archivos PDF e imágenes (JPEG, PNG, GIF, WebP)');
      return false;
    }
    if (f.size > MAX_FILE_SIZE) {
      setError(`El archivo excede el tamaño máximo de 20MB (${(f.size / 1024 / 1024).toFixed(1)}MB)`);
      return false;
    }
    return true;
  };

  const handleFileSelect = (e) => {
    const f = e.target.files[0];
    if (f && validateFile(f)) {
      setFile(f);
      setError('');
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer.files[0];
    if (f && validateFile(f)) {
      setFile(f);
      setError('');
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleUpload = async () => {
    if (!file || !docType) {
      setError('Selecciona un archivo y tipo de documento');
      return;
    }

    setUploading(true);
    setProgress(0);
    setError('');

    const formData = new FormData();
    formData.append('file', file);
    formData.append('item_number', itemNumber);
    formData.append('doc_type', docType);
    if (docReference) formData.append('doc_reference', docReference);
    if (description) formData.append('description', description);

    try {
      const uploadRes = await api.post('/documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setProgress(pct);
        }
      });

      const uploadedDocId = uploadRes.data?.document?.id || uploadRes.data?.id || null;

      // Cerrar inmediatamente y notificar éxito (sin delay)
      onUploadSuccess?.({ docReference, docType, docId: uploadedDocId });
      handleClose();
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.error || 'Error al subir el documento');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setDocType('');
    setDocReference('');
    setDescription('');
    setError('');
    setSuccess(false);
    setProgress(0);
    setUploading(false);
    onClose();
  };

  const getFileIcon = (f) => {
    if (!f) return <File size={40} />;
    if (f.type === 'application/pdf') return <FileText size={40} style={{ color: '#ef4444' }} />;
    if (f.type.startsWith('image/')) return <Image size={40} style={{ color: '#3b82f6' }} />;
    return <File size={40} />;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000,
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}
    >
      <div style={{
        background: 'white', borderRadius: '16px', width: '500px', maxWidth: '95vw',
        maxHeight: '90vh', overflow: 'auto', boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
        fontFamily: "'Space Grotesk', sans-serif"
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '20px 24px', borderBottom: '2px solid #000'
        }}>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>
            Subir Documento - Item #{itemNumber}
          </h2>
          <button onClick={handleClose} style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: '4px'
          }}>
            <X size={20} />
          </button>
        </div>

        <div style={{ padding: '24px' }}>
          {/* Drop Zone */}
          <div
            onClick={() => fileInputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            style={{
              border: `2px dashed ${isDragging ? 'var(--theme-secondary, #000)' : '#d1d5db'}`,
              borderRadius: '12px',
              padding: '32px 20px',
              textAlign: 'center',
              cursor: 'pointer',
              background: isDragging ? 'rgba(196, 255, 252, 0.2)' : file ? '#f0fdf4' : '#fafafa',
              transition: 'all 0.2s',
              marginBottom: '20px'
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            {file ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'center' }}>
                {getFileIcon(file)}
                <div style={{ textAlign: 'left' }}>
                  <div style={{ fontWeight: 600, fontSize: '14px', color: '#1f2937', wordBreak: 'break-all' }}>
                    {file.name}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>
                    {formatFileSize(file.size)}
                  </div>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); setFile(null); }}
                  style={{
                    background: '#fee2e2', border: 'none', borderRadius: '50%',
                    width: '28px', height: '28px', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}
                >
                  <X size={14} color="#ef4444" />
                </button>
              </div>
            ) : (
              <>
                <Upload size={40} style={{ color: '#9ca3af', marginBottom: '8px' }} />
                <p style={{ margin: '0 0 4px', fontWeight: 600, color: '#374151' }}>
                  Arrastra un archivo o haz clic para seleccionar
                </p>
                <p style={{ margin: 0, fontSize: '12px', color: '#9ca3af' }}>
                  PDF, JPEG, PNG, GIF, WebP (max. 20MB)
                </p>
              </>
            )}
          </div>

          {/* Doc Type */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontWeight: 600, fontSize: '13px', marginBottom: '6px', color: '#374151' }}>
              Tipo de Documento *
            </label>
            <select
              value={docType}
              onChange={(e) => handleDocTypeChange(e.target.value)}
              disabled={!!defaultDocType}
              style={{
                width: '100%', padding: '10px 12px', border: '2px solid #d1d5db',
                borderRadius: '8px', fontSize: '14px', fontFamily: "'Space Grotesk', sans-serif",
                background: defaultDocType ? '#f3f4f6' : 'white',
                cursor: defaultDocType ? 'not-allowed' : 'pointer',
                opacity: defaultDocType ? 0.8 : 1
              }}
            >
              <option value="">Seleccionar tipo...</option>
              {DOC_TYPES.map(dt => (
                <option key={dt.value} value={dt.value}>{dt.label}</option>
              ))}
            </select>
          </div>

          {/* Doc Reference */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontWeight: 600, fontSize: '13px', marginBottom: '6px', color: '#374151' }}>
              {DOC_REF_LABELS[docType]?.label || 'Referencia / N° Documento'}
            </label>
            <input
              type="text"
              value={docReference}
              onChange={(e) => setDocReference(e.target.value)}
              placeholder={DOC_REF_LABELS[docType]?.placeholder || 'Ej: COT-2024-001, FAC-12345'}
              style={{
                width: '100%', padding: '10px 12px', border: '2px solid #d1d5db',
                borderRadius: '8px', fontSize: '14px', fontFamily: "'Space Grotesk', sans-serif",
                boxSizing: 'border-box'
              }}
            />
          </div>

          {/* Description */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontWeight: 600, fontSize: '13px', marginBottom: '6px', color: '#374151' }}>
              Descripcion (opcional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Notas adicionales sobre el documento..."
              rows={2}
              style={{
                width: '100%', padding: '10px 12px', border: '2px solid #d1d5db',
                borderRadius: '8px', fontSize: '14px', fontFamily: "'Space Grotesk', sans-serif",
                resize: 'vertical', boxSizing: 'border-box'
              }}
            />
          </div>

          {/* Error */}
          {error && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca',
              borderRadius: '8px', marginBottom: '16px', color: '#dc2626', fontSize: '13px'
            }}>
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          {/* Success */}
          {success && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '10px 14px', background: '#f0fdf4', border: '1px solid #bbf7d0',
              borderRadius: '8px', marginBottom: '16px', color: '#16a34a', fontSize: '13px'
            }}>
              <CheckCircle size={16} />
              Documento subido exitosamente
            </div>
          )}

          {/* Progress Bar */}
          {uploading && (
            <div style={{ marginBottom: '16px' }}>
              <div style={{
                height: '8px', background: '#e5e7eb', borderRadius: '4px', overflow: 'hidden'
              }}>
                <div style={{
                  height: '100%', width: `${progress}%`,
                  background: 'var(--theme-secondary, #000)',
                  transition: 'width 0.3s ease', borderRadius: '4px'
                }} />
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280', textAlign: 'center', marginTop: '4px' }}>
                {progress}%
              </div>
            </div>
          )}

          {/* Upload Button */}
          <button
            onClick={handleUpload}
            disabled={!file || !docType || uploading || success}
            style={{
              width: '100%', padding: '12px',
              background: (!file || !docType || uploading || success) ? '#d1d5db' : 'var(--theme-secondary, #000)',
              color: (!file || !docType || uploading || success) ? '#9ca3af' : 'var(--theme-primary, #c4fffc)',
              border: '2px solid var(--theme-secondary, #000)',
              borderRadius: '10px', fontSize: '14px', fontWeight: 700, cursor: 'pointer',
              fontFamily: "'Space Grotesk', sans-serif",
              transition: 'all 0.2s',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
            }}
          >
            <Upload size={18} />
            {uploading ? 'Subiendo...' : success ? 'Subido' : 'Subir Documento'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DocumentUploadModal;
